import { matchupService } from '../services/matchupService';
import { territoryService } from '../services/territoryService';
import type { OwnMapOwnership, OwnMapSelection, OwnMapTeam } from '../components/ownMap/ownMapTypes';
import { buildEmpireStats, rankEmpiresByCounties } from '../components/ownMap/empireStats';
import { pickOperationName } from './memeFormats';
import type { MemeContext, MemeTeamRef } from './memeTypes';

function shortName(name: string): string {
  const cleaned = (name || '').replace(/\s+(University|Univ\.?|College)$/i, '').trim();
  const parts = cleaned.split(/\s+/);
  if (parts.length <= 2) return cleaned.slice(0, 18);
  return parts[parts.length - 1]!.slice(0, 14);
}

function teamRef(t: {
  id: number;
  name: string;
  short?: string;
  color: string;
  logo_url?: string | null;
}): MemeTeamRef {
  return {
    id: t.id,
    name: t.name,
    short: t.short || shortName(t.name),
    color: t.color || '#f97316',
    logoUrl: t.logo_url || null,
  };
}

function fromOwn(t: OwnMapTeam): MemeTeamRef {
  return teamRef({
    id: t.id,
    name: t.name,
    short: t.short,
    color: t.color,
    logo_url: t.logo_url,
  });
}

export async function buildMemeContext(opts: {
  leagueId: string;
  leagueName: string;
  ownership: OwnMapOwnership | null;
  pick: OwnMapSelection | null;
  selectedDate: string;
  selectedSeason: number | null;
  popByFips: Record<string, number | null>;
  countyLabels: Record<string, string>;
  mapBlob: Blob;
  mapUrl: string;
}): Promise<MemeContext> {
  const {
    leagueId,
    leagueName,
    ownership,
    pick,
    selectedDate,
    selectedSeason,
    popByFips,
    countyLabels,
    mapBlob,
    mapUrl,
  } = opts;

  const focused: OwnMapTeam | null =
    pick?.mode === 'territory' || pick?.mode === 'county' ? pick.team : null;

  let team: MemeTeamRef | null = focused ? fromOwn(focused) : null;
  if (!team && ownership) {
    const top = rankEmpiresByCounties(ownership)[0];
    if (top?.team) team = fromOwn(top.team);
  }

  const stats =
    ownership && team
      ? buildEmpireStats(ownership, team.id, popByFips, countyLabels)
      : null;

  const countyFips =
    pick?.mode === 'county' ? String(pick.fips).padStart(5, '0') : null;
  const countyName = pick?.mode === 'county' ? pick.name : null;
  const stateName = pick?.mode === 'county' ? pick.stateAbbr : null;

  let winner: MemeTeamRef | null = null;
  let loser: MemeTeamRef | null = null;
  let winnerScore: number | null = null;
  let loserScore: number | null = null;
  let conquestDate: string | null = null;
  let countiesFlipped: number | null = null;
  let rival: MemeTeamRef | null = null;

  if (countyFips) {
    try {
      const detail = await territoryService.getCountyDetail(leagueId, countyFips, {
        date: selectedDate || undefined,
        season: selectedSeason ?? undefined,
      });
      const latest = detail.latest_conquest;
      if (latest) {
        conquestDate = latest.game_date || latest.conquest_date;
        winnerScore = latest.winner_score;
        loserScore = latest.loser_score;
        if (latest.new_owner_team_id != null && latest.new_owner_name) {
          winner = teamRef({
            id: latest.new_owner_team_id,
            name: latest.winner_name || latest.new_owner_name,
            color: latest.new_owner_color || '#f97316',
            logo_url: latest.new_owner_logo,
          });
        }
        if (latest.previous_owner_team_id != null && latest.previous_owner_name) {
          loser = teamRef({
            id: latest.previous_owner_team_id,
            name: latest.loser_name || latest.previous_owner_name,
            color: latest.previous_owner_color || '#64748b',
            logo_url: latest.previous_owner_logo,
          });
        } else if (latest.loser_name) {
          loser = teamRef({
            id: -1,
            name: latest.loser_name,
            color: '#64748b',
            logo_url: null,
          });
        }
        if (latest.game_id != null) {
          const sameGame = (detail.history || []).filter((h) => h.game_id === latest.game_id);
          if (sameGame.length > 1) countiesFlipped = sameGame.length;
          else countiesFlipped = 1;
        }
      }
      if (detail.current_owner && !team) {
        team = teamRef({
          id: detail.current_owner.team_id,
          name: detail.current_owner.team_name,
          short: detail.current_owner.short_name || undefined,
          color: detail.current_owner.map_color || detail.current_owner.primary_color || '#f97316',
          logo_url: detail.current_owner.logo_url,
        });
      }
    } catch {
      /* county detail optional */
    }
  }

  if (winner && loser) {
    rival = team?.id === winner.id ? loser : winner;
  }

  if (team && !countiesFlipped) {
    try {
      const tidbits = await territoryService.getTeamTidbits(leagueId, team.id);
      const swing = tidbits.biggest_win;
      if (swing?.counties) countiesFlipped = swing.counties;
      if (!loser && swing?.foe_name) {
        loser = teamRef({
          id: swing.foe_team_id || -1,
          name: swing.foe_name,
          short: swing.foe_short || undefined,
          color: '#64748b',
          logo_url: null,
        });
        if (!winner) winner = team;
        if (!conquestDate) conquestDate = swing.date;
        if (winnerScore == null) winnerScore = swing.winner_score;
        if (loserScore == null) loserScore = swing.loser_score;
      }
    } catch {
      /* optional */
    }
  }

  let nextGame: MemeContext['nextGame'] = null;
  if (team) {
    const teamId = team.id;
    try {
      const featured = await matchupService.getFeaturedMatchups(leagueId, { limit: 24 });
      const hit = (featured.matchups || []).find((m) => {
        const home = Number(m.home_team?.team_id);
        const away = Number(m.away_team?.team_id);
        return home === teamId || away === teamId;
      });
      if (hit) {
        const homeId = Number(hit.home_team?.team_id);
        const awayTeam = hit.away_team;
        const homeTeam = hit.home_team;
        const opp = homeId === teamId ? awayTeam : homeTeam;
        const oppName = opp?.team_name || 'Opponent';
        nextGame = {
          opponentName: shortName(String(oppName)),
          gameDate: hit.game_date || null,
          venue: hit.venue || null,
        };
        if (!rival && opp) {
          rival = teamRef({
            id: Number(opp.team_id) || -2,
            name: String(oppName),
            short: opp.short_name,
            color: opp.primary_color || '#64748b',
            logo_url: opp.logo_url,
          });
        }
      }
    } catch {
      /* schedule optional */
    }
  }

  const isZeroLand = Boolean(team && (stats?.counties ?? 0) === 0);
  const seed = `${team?.short || 'X'}-${countyName || selectedDate || 'map'}`;
  const operationName = pickOperationName(seed);

  return {
    leagueId,
    leagueName,
    seasonYear: selectedSeason,
    date: selectedDate || null,
    mapUrl,
    countyFips,
    countyName,
    stateName,
    team,
    rival,
    winner,
    loser,
    winnerScore,
    loserScore,
    conquestDate,
    countiesFlipped,
    teamCounties: stats?.counties ?? null,
    teamLandShare: stats?.countyShare ?? null,
    teamPopulation: stats?.population ?? null,
    isZeroLand,
    nextGame,
    operationName,
    mapBlob,
  };
}
